<script setup lang="ts">
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { DropZoneData } from '@/types/components/DropZoneData'
import { EHubProvider, type EventDetailProvider } from '@/types/enum/HubProviderEnum'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import type { PermissionsEnums } from '@/types/permissions/PermissionsEnum'
import type { SocketCheckpointDetectionModel } from '@/types/socket/SocketDetectionModel'
import CheckpointDetailView from '@/views/CheckpointsView/CheckpointDetailView.vue'
import CheckpointCardMode from '@/views/EventsView/Detail/CheckpointsTable/CheckpointCardMode.vue'
import EventCreateCheckpoint from '@/views/EventsView/Detail/CreateCheckpoint/EventCreateCheckpoint.vue'
import {
    DebouncedInputField,
    PermissionWrapper,
    StackLayout,
    TypographyItem
} from '@rfc/rfc-components-library'
import { EDetectionActions, EDeviceActions } from '@rfc/rfc-components-library/store'
import type { PaginationDataType } from '@rfc/rfc-components-library/types'
import { type Position } from '@vueuse/core'
import { computed, inject, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { buildDetectionArray } from '../../../../socket/socketUtils'
import { EEventActions } from '../../../../store/eventStore/EventStoreTypes'
import CheckpointOrderEdit from '../CheckpointsTable/components/CheckpointOrderEdit.vue'
import CheckpointStats from './CheckpointStats.vue'

const store = useStore()

type Props = {
    partnerConfig?: PartnerModel
    detections: SocketCheckpointDetectionModel[]
    eventData: EventModel
    dropZone?: DropZoneData
    deletePermission: PermissionsEnums
}
const props = defineProps<Props>()

type Emits = {
    (e: 'checkpointUpdate', checkpoints: CheckpointModel[]): void
    (e: 'dragStart', device?: AssociatedDeviceModel, checkpointId?: number): void
    (e: 'dragEnd', position?: Position): void
    (e: 'refresh'): void
    (e: 'close'): void
}
const emit = defineEmits<Emits>()

const route = useRoute()

const eventId = computed(() => parseInt(route.params.event_id.toString()))
const checkpoint = ref<CheckpointModel>()
const data = computed(() => store?.state.checkpoint)
const latestDetections = ref<SocketCheckpointDetectionModel[]>([])
const associatedDevices = ref<Partial<DeviceModel>[]>([])

const checkpointNameFilter = ref<string>()
const handleCheckpointFilter = (value?: string) => {
    checkpointNameFilter.value = value
    getCheckpoints(value, false)
}

const { setDevices } = inject<EventDetailProvider>(EHubProvider.EVENT_DEVICES, {})

watch(
    () => props.detections,
    newDetection => (latestDetections.value = newDetection),
    { immediate: true, deep: true }
)

const getCheckpoints = (name?: string, noRefreshDevices?: boolean) =>
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.FETCH_CHECKPOINTS}`, {
            id: eventId.value,
            params: {
                filters: { name },
                per_page: -1
            }
        })
        ?.then((res: CheckpointModel[]) => {
            latestDetections.value = res.map(item => ({
                checkpoint_id: item.id,
                detections: []
            }))
            emit('checkpointUpdate', res)

            if (noRefreshDevices) return
            getDevices()
        })

const getDevices = () =>
    store
        ?.dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.FETCH_API_DEVICES}`, {
            per_page: -1,
            filters: { event_id: eventId.value }
        })
        ?.then((res: PaginationDataType<DeviceModel[]>) => {
            associatedDevices.value = res.data ?? []
            setDevices?.(associatedDevices.value)

            data.value?.data?.checkpoints.forEach((itemCheckpoint: CheckpointModel) => {
                const filteredDevices =
                    res.data?.filter(item => item.checkpoint?.id === itemCheckpoint.id) ?? []
                itemCheckpoint.associates = itemCheckpoint.associates?.map(itemAssociate => ({
                    ...itemAssociate,
                    ...(filteredDevices.find(
                        filteredDevice =>
                            (filteredDevice.device_id ?? filteredDevice.id) ===
                            itemAssociate.device_id
                    ) ?? {})
                }))
            })

            if (!latestDetections.value?.length)
                store
                    ?.dispatch(
                        `${EStoreModules.DETECTION}/${EDetectionActions.GET_LATEST_DETECTIONS}`,
                        {
                            device_ids: res.data?.map(item => item.device_id ?? item.id)
                        }
                    )
                    .then(
                        resDetections =>
                            (latestDetections.value = buildDetectionArray(
                                resDetections,
                                latestDetections.value,
                                'checkpoint_id'
                            ))
                    )
        })

onMounted(() => getCheckpoints())

const handleDeleteCheckpoint = (value: CheckpointModel) =>
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.DELETE_CHECKPOINT}`, {
            eventId: eventId.value,
            checkpointId: value.id,
            name: value.name
        })
        .then(() => {
            checkpoint.value = undefined
            getCheckpoints()
        })

const handleAssociateDevice = () => getCheckpoints()

const handleRefresh = () => {
    getCheckpoints()
    emit('refresh')
}

const orderEdit = ref(false)
provide('orderEdit', orderEdit)

const handleOrderClick = (value: boolean) => {
    orderEdit.value = value
    if (value) return
    return saveOrder()
}

const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)

const checkpointOrder = ref<number[]>()
const saveOrder = () => {
    if (!checkpointOrder.value) return
    store
        ?.dispatch(`${EStoreModules.EVENT}/${EEventActions.SET_CHECKPOINTS_POSITIONS}`, {
            eventId: eventId.value,
            checkpointOrder: checkpointOrder.value
        })
        .then(() => getCheckpoints(undefined, true))
}
</script>

<template>
    <StackLayout class="section-box w-full" direction="column" v-bind="$attrs">
        <StackLayout class="section-header space-between align-center">
            <TypographyItem
                :label="$t('event.detail.checkpoints.checkpointsTitle')"
                class="section-text" />
            <StackLayout v-if="!isViewerOnly" direction="row" gap="8">
                <PermissionWrapper :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
                    <CheckpointOrderEdit
                        v-if="data?.data?.checkpoints && data?.data?.checkpoints?.length > 2"
                        @click="handleOrderClick"
                        class="origin-right" />
                    <EventCreateCheckpoint
                        :eventId="eventId"
                        :partnerConfig="partnerConfig"
                        :loading="!!store?.state.checkpoint.loading"
                        @refresh="getCheckpoints"
                        class="origin-left" />
                </PermissionWrapper>
            </StackLayout>
        </StackLayout>
        <StackLayout direction="column" class="checkpoint-section-content">
            <CheckpointStats
                :checkpoints="data?.data?.checkpoints"
                :eventData="eventData"
                class="justify-evenly pt-6 pb-5" />
            <StackLayout direction="column" :gap="4">
                <DebouncedInputField
                    :label="$t('checkpoint.filters.name')"
                    :modelValue="checkpointNameFilter"
                    @change="handleCheckpointFilter"
                    class="px-4 w-fit" />
                <StackLayout class="checkpoints-list space-between">
                    <CheckpointCardMode
                        :sync="eventData.sync"
                        :data="data?.data?.checkpoints"
                        :detections="latestDetections"
                        :partnerConfig="partnerConfig"
                        :dropZone="dropZone"
                        :editPermission="ECheckpointsPerm.CHECKPOINTS_EDIT"
                        :deletePermission="ECheckpointsPerm.CHECKPOINTS_DELETE"
                        @dragStart="(val1, val2) => $emit('dragStart', val1, val2)"
                        @dragEnd="$emit('dragEnd', $event)"
                        @close="$emit('close')"
                        @goTo="checkpoint = $event"
                        @delete="handleDeleteCheckpoint"
                        @refresh="handleRefresh"
                        @setCheckpointsOrder="checkpointOrder = $event" />
                    <CheckpointDetailView
                        v-if="checkpoint"
                        :open="!!checkpoint"
                        :sync="eventData.sync"
                        :loading="!!store?.state.checkpoint.loading"
                        :deletePermission="ECheckpointsPerm.CHECKPOINTS_DELETE"
                        :checkpoint="checkpoint"
                        :partnerConfig="partnerConfig"
                        class="w-full"
                        @delete="handleDeleteCheckpoint"
                        @refresh="handleRefresh"
                        @refreshAssociate="handleAssociateDevice"
                        @close="checkpoint = undefined" />
                </StackLayout>
            </StackLayout>
        </StackLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.checkpoints-list {
    padding: 1em;
}

@media (min-width: 761px) {
    .checkpoint-section-content {
        overflow-y: auto;
    }
}
</style>
